import type { FC } from "react"
import { useForm } from "react-hook-form"

import { TransferProductCostCompleted, type TransferStatusFilterValue } from "../../constants"
import type { TransferSearchParams } from "../../schemas/transfer"

import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import type { TransferType } from "@/graphql/codegen/graphql"
import {
  InventoryDateRangeFilter,
  InventoryFilterList,
  InventoryFilterRadioGroup,
  InventoryFilterSearchableList,
} from "@/screens/Inventory/components"
import type { Option } from "@/types/form-fields"

interface TransferFilterDrawerProps {
  open: boolean
  searchParams: TransferSearchParams
  typeOptions: Option<TransferType>[]
  statusOptions: Option<TransferStatusFilterValue>[]
  productCostCompletedOptions: Option<TransferProductCostCompleted>[]
  warehouseOptions: Option<string>[]
  createdByOptions: Option<string>[]
  onToggle: () => void
  onChange: <K extends keyof TransferSearchParams>(key: K, value: TransferSearchParams[K]) => void
  onClear: () => void
}

export const TransferFilterDrawer: FC<TransferFilterDrawerProps> = ({
  open,
  searchParams,
  typeOptions,
  statusOptions,
  productCostCompletedOptions,
  warehouseOptions,
  createdByOptions,
  onToggle,
  onChange,
  onClear,
}) => {
  const types = searchParams.types ?? []
  const statuses = searchParams.statuses ?? []
  const sourceWarehouseAreaIds = searchParams.sourceWarehouseAreaIds ?? []
  const targetWarehouseAreaIds = searchParams.targetWarehouseAreaIds ?? []
  const createdByUserIds = searchParams.createdByUserIds ?? []
  const productCostCompleted = searchParams.productCostCompleted ?? TransferProductCostCompleted.All
  const createdOnFrom = searchParams.createdOnFrom ?? null
  const createdOnTo = searchParams.createdOnTo ?? null

  const { control } = useForm({
    defaultValues: {
      types,
      statuses,
      sourceWarehouseAreaIds,
      targetWarehouseAreaIds,
      createdByUserIds,
    },
  })

  return (
    <SearchFilterDrawer open={open} toggleDrawer={onToggle} clear={onClear} testId='transfer.filter-drawer'>
      <div className='flex h-full flex-col gap-y-4 overflow-y-auto pb-4'>
        <InventoryFilterList
          label='Type'
          control={control}
          name='types'
          values={types}
          options={typeOptions}
          onChange={(values) => onChange("types", values)}
          testId='transfer.type-filter'
        />

        <InventoryFilterSearchableList
          label='From Warehouse'
          control={control}
          name='sourceWarehouseAreaIds'
          values={sourceWarehouseAreaIds}
          options={warehouseOptions}
          onChange={(values) => onChange("sourceWarehouseAreaIds", values)}
          testId='transfer.from-warehouse-filter'
        />

        <InventoryFilterSearchableList
          label='To Warehouse'
          control={control}
          name='targetWarehouseAreaIds'
          values={targetWarehouseAreaIds}
          options={warehouseOptions}
          onChange={(values) => onChange("targetWarehouseAreaIds", values)}
          testId='transfer.to-warehouse-filter'
        />

        <InventoryDateRangeFilter
          label='Created Date'
          dateFrom={createdOnFrom}
          dateTo={createdOnTo}
          onDateFromChange={(value) => onChange("createdOnFrom", value)}
          onDateToChange={(value) => onChange("createdOnTo", value)}
          testId='transfer.created-date-filter'
        />

        <InventoryFilterList
          label='Status'
          control={control}
          name='statuses'
          values={statuses}
          options={statusOptions}
          onChange={(values) => onChange("statuses", values)}
          testId='transfer.status-filter'
        />

        <InventoryFilterSearchableList
          label='Created By'
          control={control}
          name='createdByUserIds'
          values={createdByUserIds}
          options={createdByOptions}
          onChange={(values) => onChange("createdByUserIds", values)}
          testId='transfer.created-by-filter'
        />

        <InventoryFilterRadioGroup
          label='Product Cost Completed'
          value={productCostCompleted}
          options={productCostCompletedOptions}
          onChange={(value) =>
            onChange("productCostCompleted", value === TransferProductCostCompleted.All ? undefined : value)
          }
          testIdPrefix='transfer.product-cost-completed-filter'
        />
      </div>
    </SearchFilterDrawer>
  )
}
